/**
 * Dense preview content for a specific demo demand record so History,
 * attorney Messages, and Notes can be visually reviewed with long lists.
 *
 * Message/history actors follow the demand’s real debtor + attorney names
 * (m_demo_2282 is Avery Turner / Morgan Cole in the demo seed — not a
 * hard-coded Blake Myers thread).
 */

import { createHistoryEvent, HISTORY_EVENT_TYPES, setLocalMatterHistory } from './matterHistory.js';
import { seedDemoMessages } from './communicationsStore.js';

export const MATTER_DETAIL_PREVIEW_ID = 'm_demo_2282';
const NOTES_KEY = 'demandjet.matterNotes';
const MESSAGES_KEY = 'demandjet.communications';

const DEFAULT_PREVIEW_MATTER = Object.freeze({
  ROWID: MATTER_DETAIL_PREVIEW_ID,
  Debtor_Name: 'Avery Turner',
  Debtor_Company: 'Redwood Supply Chain',
  Debtor_Email: 'avery.turner@redwoodsupply.demo',
  Attorney_Name: 'Morgan Cole, Esq.',
  Attorney_Email: 'morgan.cole@partner.demo',
  External_Invoice_ID: 'DEMO-INV-3241',
  Invoice_ID: 'DEMO-INV-3241',
  External_Account_Number: 'ACCT-88421',
  Amount_Owed: 29284.5,
  Tracking_Number: '9400111899223344556677',
});

function previewActors(matter = {}) {
  const clientName =
    String(matter.Debtor_Name || '').trim() || DEFAULT_PREVIEW_MATTER.Debtor_Name;
  const attorneyName =
    String(matter.Attorney_Name || '').trim() || DEFAULT_PREVIEW_MATTER.Attorney_Name;
  const company =
    String(matter.Debtor_Company || '').trim() || DEFAULT_PREVIEW_MATTER.Debtor_Company;
  const invoice =
    String(matter.External_Invoice_ID || matter.Invoice_ID || '').trim() ||
    DEFAULT_PREVIEW_MATTER.External_Invoice_ID;
  const amount = Number(matter.Amount_Owed);
  const amountLabel = Number.isFinite(amount)
    ? amount.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '$29,284.50';
  const tracking =
    String(matter.Tracking_Number || '').trim() || DEFAULT_PREVIEW_MATTER.Tracking_Number;
  const clientEmail =
    String(matter.Debtor_Email || '').trim() || DEFAULT_PREVIEW_MATTER.Debtor_Email;
  const attorneyEmail =
    String(matter.Attorney_Email || '').trim() || DEFAULT_PREVIEW_MATTER.Attorney_Email;
  return {
    clientName,
    attorneyName,
    company,
    invoice,
    amountLabel,
    tracking,
    clientEmail,
    attorneyEmail,
  };
}

function historySummaries(actors) {
  const { clientName, attorneyName, company, invoice, amountLabel, tracking, clientEmail } =
    actors;
  return [
    ['CREATED', 'Demand created'],
    [
      'RECORD_UPDATED',
      `Updated company (${company}) and contact name (${clientName})`,
    ],
    ['RECORD_UPDATED', `Updated amount owed ($0.00 → ${amountLabel})`],
    ['EVIDENCE_UPDATED', 'Updated evidence (none → invoice-3241.pdf)'],
    ['ATTORNEY_ASSIGNED', `Assigned attorney partner ${attorneyName}`],
    ['STATUS_CHANGED', 'Status changed to Assigned'],
    ['MESSAGE_SENT', `Client messaged attorney about invoice ${invoice}`],
    ['RECORD_UPDATED', 'Updated street (12 Ridge Rd) and city (Boulder)'],
    [
      'EVIDENCE_UPDATED',
      'Updated evidence (invoice-3241.pdf → invoice-3241.pdf, remittance-advice.pdf)',
    ],
    ['MESSAGE_SENT', 'Attorney requested certified-mail timeline confirmation'],
    ['STATUS_CHANGED', 'Status changed to Sent'],
    ['RECORD_UPDATED', `Updated tracking number (${tracking})`],
    ['MESSAGE_SENT', 'Client confirmed delivery expectation for Friday'],
    ['STATUS_CHANGED', 'Status changed to Delivered'],
    ['RECORD_UPDATED', 'Updated dispute type (Nonpayment)'],
    ['MESSAGE_SENT', 'Attorney shared draft follow-up language'],
    ['RECORD_UPDATED', 'Updated payment plan (No → Yes · Net 30)'],
    ['MESSAGE_SENT', 'Client asked about installment options'],
    ['STATUS_CHANGED', 'Status changed to Unpaid'],
    ['RECORD_UPDATED', `Updated email (${clientEmail})`],
  ];
}

function messageThread(actors) {
  const { clientName, attorneyName, invoice } = actors;
  return [
    {
      role: 'CLIENT',
      name: clientName,
      body: `${attorneyName.split(',')[0]} — invoice ${invoice} is still unpaid. Can you review the evidence pack and advise next steps?`,
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Received. I reviewed the invoice and remittance notes. I’ll prepare the demand draft today.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Thanks. Debtor said they “never got the original invoice,” but we have delivery confirmation.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'That helps. Please keep the remittance advice attached — we’ll reference it in the demand.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Attached. Also noting they asked about a short payment plan if we escalate.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Understood. I’ll keep installment language optional so you stay in control of acceptance.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Demand letter is ready for review. Tracking will be added once certified mail is dispatched.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Looks good — please send. Prefer USPS certified with signature.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Dispatched. Tracking number is in the demand record. I’ll update you when delivery scans.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Delivery confirmed on our end too. Any response window before unpaid escalation?',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Standard response window is running. If there’s no substantive reply, we can move to unpaid.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'They offered a partial payment verbally. Should we document that in notes only for now?',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Yes — capture it in Notes with date/time. Don’t treat it as accepted until it’s in writing.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Logged. They also asked whether Net 30 installments are possible.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'You can enable payment-plan acceptance on the demand if you want that option open.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Enabled Net 30. Still no written commitment from the debtor.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Then we stay on the unpaid path. I’ll prepare a concise follow-up demand.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Please do. Also flag if we should loop in litigation-ready status next week.',
    },
    {
      role: 'ATTORNEY',
      name: attorneyName,
      body: 'Will do. If silence continues after the follow-up, litigation-ready is the next recommended step.',
    },
    {
      role: 'CLIENT',
      name: clientName,
      body: 'Perfect — thanks for staying on top of this thread.',
    },
  ];
}

function noteBodies(actors) {
  const { clientName, attorneyName } = actors;
  return [
    [clientName, 'Debtor claimed invoice never received; we have delivery confirmation on file.'],
    [attorneyName, 'Reminded client to keep remittance advice with the evidence pack.'],
    [clientName, 'Verbal partial-payment offer on 9/12 — not accepted; waiting on written terms.'],
    ['Casey Ortiz', 'AP contact prefers email before phone. Best window 2–4pm MT.'],
    [clientName, 'Enabled Net 30 installment option in case they come back with a plan.'],
    [attorneyName, 'Follow-up demand drafted; hold send until Friday if no written reply.'],
    [clientName, 'Internal: do not waive demand fee if they pay after attorney assignment.'],
    ['Dana Cho', 'Collections lead briefed; escalate to unpaid if no response by end of week.'],
    [clientName, 'Tracking scanned as delivered; signature on file with USPS.'],
    [attorneyName, 'Client okayed certified mail language — no edits needed on next send.'],
  ];
}

function daysAgo(days, hours = 10, minutes = 0) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function buildHistory(matterId, actors) {
  const summaries = historySummaries(actors);
  return summaries.map(([type, summary], index) => {
    const createdAt = daysAgo(summaries.length - index, 9 + (index % 6), (index * 7) % 50);
    const actorName =
      type === 'MESSAGE_SENT' || type === 'ATTORNEY_ASSIGNED' || type === 'STATUS_CHANGED'
        ? index % 2 === 0
          ? actors.clientName
          : actors.attorneyName
        : actors.clientName;
    return createHistoryEvent({
      matterId,
      type: HISTORY_EVENT_TYPES[type] || type,
      summary,
      actorName,
      actorRole: actorName === actors.attorneyName ? 'ATTORNEY' : 'CLIENT',
      createdAt,
      id: `mev_preview_${matterId}_${index + 1}`,
    });
  });
}

function buildMessages(matterId, actors) {
  const thread = messageThread(actors);
  return thread.map((item, index) => ({
    ROWID: `msg_preview_${matterId}_${index + 1}`,
    Matter_ID: matterId,
    Sender_Role: item.role,
    Sender_Name: item.name,
    Body: item.body,
    Created_At: daysAgo(thread.length - index, 11 + (index % 5), (index * 11) % 55),
  }));
}

function buildNotes(actors) {
  const bodies = noteBodies(actors);
  return bodies
    .map(([authorName, body], index) => ({
      id: `note_preview_${MATTER_DETAIL_PREVIEW_ID}_${index + 1}`,
      body,
      createdAt: daysAgo(bodies.length - index, 14, (index * 13) % 50),
      authorName,
      authorEmail:
        authorName === actors.attorneyName
          ? actors.attorneyEmail
          : authorName === actors.clientName
            ? actors.clientEmail
            : `${authorName.toLowerCase().replace(/\s+/g, '.')}@demandjet.demo`,
    }))
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

/**
 * Force-seed dense History / Messages / Notes for the preview demand.
 * Safe to call repeatedly; overwrites only this matter id’s local preview stores.
 * @param {string} [matterId]
 * @param {object} [matter] Demand row — used so sender names match the debtor/attorney.
 */
export function seedMatterDetailPreview(
  matterId = MATTER_DETAIL_PREVIEW_ID,
  matter = null
) {
  const id = String(matterId || '');
  if (id !== MATTER_DETAIL_PREVIEW_ID) {
    return {
      history: [],
      messages: [],
      notes: [],
    };
  }

  const actors = previewActors(matter || DEFAULT_PREVIEW_MATTER);

  const history = buildHistory(id, actors);
  setLocalMatterHistory(id, history);

  const messages = buildMessages(id, actors);
  const messageMap = readJson(MESSAGES_KEY, {});
  messageMap[id] = messages;
  writeJson(MESSAGES_KEY, messageMap);
  // Keep seedDemoMessages path warm for callers that only fill empty threads.
  seedDemoMessages(id, messages);

  const notes = buildNotes(actors);
  const notesMap = readJson(NOTES_KEY, {});
  notesMap[id] = { entries: notes, updatedAt: notes[0]?.createdAt || new Date().toISOString() };
  writeJson(NOTES_KEY, notesMap);

  return { history, messages, notes };
}

export function isMatterDetailPreviewId(matterId) {
  return String(matterId || '') === MATTER_DETAIL_PREVIEW_ID;
}
